<script lang="ts" setup>
import Laptop from '~/assets/images/noriulive/laptop.png'
import iPhone from '~/assets/images/noriulive/iphone_mock.png'

import AndYou from '~/assets/images/noriulive/and_you.svg'
import { useStoreConfig } from '~/store/storeConfig'

const props = defineProps<{
  youtubeLink?: string
  fbSrc?: string
  videoType: 'Facebook' | 'Youtube'
}>()

const { t } = useLang()
const { isMobile } = useStoreConfig()
const isYoutube = ref(props.videoType === 'Youtube')

// Disabled until updated to nuxt >=3.16
const getYtVideoId = () => {
  if (isYoutube.value && props.youtubeLink) {
    try {
      const urlObj = new URL(props.youtubeLink)

      return urlObj.searchParams.get('v')
    } catch (error) {
      return null
    }
  }

  return null
}

const getEmbedSrc = () => {
  if (isYoutube.value && props.youtubeLink) {
    try {
      if (props.youtubeLink.toLowerCase().includes('shorts')) {
        ytVideoId.value = null

        return props.youtubeLink.replace('/shorts/', '/embed/')
      }

      if (props.youtubeLink.toLowerCase().includes('embed')) {
        ytVideoId.value = null

        return props.youtubeLink
      }

      return null
    } catch (error) {
      return null
    }
  }

  if (!isYoutube.value && props.fbSrc) {
    return props.fbSrc
  }
}

const ytVideoId = ref(getYtVideoId())
const embedSrc = ref(getEmbedSrc())

const fbIframeSize = computed(() => ({
  h: isMobile ? 450 : 550,
  w: isMobile ? 210 : 257,
}))

const iframeSrc = computed(() => {
  if (embedSrc.value) {
    const urlObj = new URL(embedSrc.value)

    urlObj.searchParams.set('width', fbIframeSize.value.w.toString())
    urlObj.searchParams.set('height', fbIframeSize.value.h.toString())

    return urlObj.toString()
  }
})

const isLoaded = ref(false)
const isPlaying = ref(false)
const video = ref()

function stateChange(event) {
  isPlaying.value = event.data === 1
}
</script>

<template>
  <div class="flex gap-10 flex-wrap justify-center py-10 lg:py-20 flex-col xl:flex-row">
    <div class="flex flex-col gap-2 items-center justify-center p-2 lg:p-4">
      <div class="font-bold text-[26px] lg:text-[45px] text-center">{{ t('home.video-promo-title') }}</div>
      <img :src="AndYou" class="h-[66px] lg:h-[144px]" />
    </div>
    <div class="relative flex justify-center">
      <img :src="iPhone" class="min-h-[500px] max-h-[500px] lg:max-h-[600px] z-[2] pointer-events-none" />
      <div
        v-if="ytVideoId"
        class="yt-player-wrapper absolute lg:top-[17px] lg:max-h-[545px] lg:max-w-[263px] w-full h-full rounded-full">
        <ScriptYouTubePlayer
          ref="video"
          :video-id="ytVideoId"
          trigger="visible"
          @ready="isLoaded = true"
          @state-change="stateChange">
          <template #awaitingLoad>
            <div class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-[48px] w-[68px]">
              <svg height="100%" version="1.1" viewBox="0 0 68 48" width="100%">
                <path
                  d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                  fill="#f00" />
                <path d="M 45,24 27,14 27,34" fill="#fff" />
              </svg>
            </div>
          </template>
        </ScriptYouTubePlayer>
      </div>

      <div v-else class="embed-player-wrapper">
        <div
          class="embed-player z-[3] flex justify-center bg-black overflow-hidden items-center rounded-[27px] lg:rounded-[30px]">
          <template v-if="iframeSrc">
            <iframe
              :src="iframeSrc"
              style="border: none; overflow: hidden"
              scrolling="no"
              :width="fbIframeSize.w"
              :height="fbIframeSize.h"
              frameborder="0"
              allowfullscreen="true"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.yt-player-wrapper {
  @apply max-w-[215px] top-[13px] overflow-hidden;
}

.yt-player-wrapper {
  > div {
    width: 100% !important;
    height: 100% !important;
    aspect-ratio: auto !important;
  }
}

.embed-player-wrapper {
  @apply max-w-[215px] max-h-[457px] top-[13px] lg:bg-black absolute lg:top-[16px] lg:max-h-[550px] lg:max-w-[263px] w-full h-full rounded-[25px] lg:rounded-[30px] overflow-hidden;

  .embed-player {
    @apply h-full flex justify-center;

    > iframe {
      aspect-ratio: 1 / 1.25;
    }
  }
}
</style>
